import { Row, Table, TableSummary, Title } from "melco-ui";
import { StatisticsConfig } from "../../../hooks/reporting/statistics/useStatistics";
import { useStatisticsNumberFormatter } from "../../../hooks/reporting/statistics/useStatisticsNumberFormatter";
import { DownloadReportsButton } from "./DownloadReportsButton";
import {
  ReportingMelcoMachineStatisticsModelsStatisticListItem,
  ReportingMelcoModelsGenericList1MelcoMachineStatisticsModelsStatisticListItem,
} from "melco-shared-logic";
import { useTranslation } from "react-i18next";
import { LockedFeature } from "../../overlay/LockedFeature";
import { EnableProReportingButton } from "../proReporting/EnableProReportingButton";

type DetailedStatisticsProps = {
  dataConfig?: StatisticsConfig;
  data?: ReportingMelcoModelsGenericList1MelcoMachineStatisticsModelsStatisticListItem;
  isLoading?: boolean;
  isMachineReportingAllowed: boolean;
};

export const DetailedStatistics: React.FC<DetailedStatisticsProps> = ({
  dataConfig,
  data,
  isLoading,
  isMachineReportingAllowed,
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useStatisticsNumberFormatter();

  const columns = [
    {
      dataIndex: dataConfig?.grouped ? "group_name" : "machine_name",
      title: dataConfig?.grouped
        ? t("reporting.statistics.detailedtable.group_name")
        : t("reporting.statistics.detailedtable.machine_name"),
      ellipsis: true,
      width: 150,
      sorter: (
        a: ReportingMelcoMachineStatisticsModelsStatisticListItem,
        b: ReportingMelcoMachineStatisticsModelsStatisticListItem
      ) => {
        // Use the appropriate field (group_name or machine_name) based on the grouped configuration
        const key = dataConfig?.grouped ? "group_name" : "machine_name";

        // Fallback to empty string if machine_name is null or undefined
        const aValue = a[key] ?? ""; // Default to empty string if a[key] is null or undefined
        const bValue = b[key] ?? ""; // Default to empty string if b[key] is null or undefined

        return aValue.localeCompare(bValue);
      },
    },
    {
      dataIndex: "stitches",
      title: t("reporting.statistics.detailedtable.stitches"),
      render: (value: number) => formatNumber(value),
      align: "right" as const,
      sorter: (
        a: ReportingMelcoMachineStatisticsModelsStatisticListItem,
        b: ReportingMelcoMachineStatisticsModelsStatisticListItem
      ) => (a.stitches ?? 0) - (b.stitches ?? 0),
      // width: 150,
    },
    {
      dataIndex: "runs",
      title: t("reporting.statistics.detailedtable.runs"),
      render: (value: number) => formatNumber(value),
      align: "right" as const,
      sorter: (
        a: ReportingMelcoMachineStatisticsModelsStatisticListItem,
        b: ReportingMelcoMachineStatisticsModelsStatisticListItem
      ) => (a.runs ?? 0) - (b.runs ?? 0),
      width: 80
    },
    {
      dataIndex: "thread_breaks",
      title: t("reporting.statistics.detailedtable.thread_breaks"),
      render: (value: number) => formatNumber(value),
      align: "right" as const,
      sorter: (
        a: ReportingMelcoMachineStatisticsModelsStatisticListItem,
        b: ReportingMelcoMachineStatisticsModelsStatisticListItem
      ) => (a.thread_breaks ?? 0) - (b.thread_breaks ?? 0),
    },
    {
      dataIndex: "total_running_time",
      title: t("reporting.statistics.detailedtable.total_running_time"),
      render: (value: number) => formatNumber(value),
      align: "right" as const,
      sorter: (
        a: ReportingMelcoMachineStatisticsModelsStatisticListItem,
        b: ReportingMelcoMachineStatisticsModelsStatisticListItem
      ) => (a.total_running_time ?? 0) - (b.total_running_time ?? 0),
    },
    {
      dataIndex: "total_idle_time",
      title: t("reporting.statistics.detailedtable.total_idle_time"),
      render: (value: number) => formatNumber(value),
      align: "right" as const,
      sorter: (
        a: ReportingMelcoMachineStatisticsModelsStatisticListItem,
        b: ReportingMelcoMachineStatisticsModelsStatisticListItem
      ) => (a.total_idle_time ?? 0) - (b.total_idle_time ?? 0),
    },
    {
      dataIndex: "total_standby_time",
      title: t("reporting.statistics.detailedtable.total_standby_time"),
      render: (value: number) => formatNumber(value),
      align: "right" as const,
      sorter: (
        a: ReportingMelcoMachineStatisticsModelsStatisticListItem,
        b: ReportingMelcoMachineStatisticsModelsStatisticListItem
      ) => (a.total_standby_time ?? 0) - (b.total_standby_time ?? 0),
    },
  ];

  return (
    <>
      <Row justify="space-between">
        <Title level={5}>{t('reporting.statistics.detailedtable.title')}</Title>
        <DownloadReportsButton
          disabled={isMachineReportingAllowed ? false : true}
          dataConfig={dataConfig}
        />
      </Row>
      <LockedFeature
        label={t('reporting.statistics.enable_pro_reporting')}
        isAllowed={isMachineReportingAllowed}
        actionComponent={
          <EnableProReportingButton
            text={t("reporting.proreporting.enablebutton")}
          />
        }
      >
        <Table
          loading={isLoading}
          // dataSource={
          //   dataConfig?.fakeData ? statiscticsFakeData : data?.list ?? []
          // }
          dataSource={data?.list ?? []}
          columns={columns}
          pagination={false}
          noDataMessage={t("reporting.statistics.charts.no_data_available")}
          summary={(pageData) => {
            const totalStitches = pageData.reduce(
              (sum, current) => sum + (current?.stitches ?? 0),
              0
            );
            const totalRuns = pageData.reduce(
              (sum, current) => sum + (current?.runs ?? 0),
              0
            );
            const totalBreaks = pageData.reduce(
              (sum, current) => sum + (current?.thread_breaks ?? 0),
              0
            );
            const total_running_time = pageData.reduce(
              (sum, current) => sum + (current?.total_running_time ?? 0),
              0
            );
            const total_idle_time = pageData.reduce(
              (sum, current) => sum + (current?.total_idle_time ?? 0),
              0
            );
            const total_standby_time = pageData.reduce(
              (sum, current) => sum + (current?.total_standby_time ?? 0),
              0
            );
            return (
              <TableSummary fixed>
                <TableSummary.Row>
                  <TableSummary.Cell index={0}>{t('reporting.statistics.detailedtable.total')}</TableSummary.Cell>
                  <TableSummary.Cell index={1} align="right">
                    {formatNumber(totalStitches)}
                  </TableSummary.Cell>
                  <TableSummary.Cell index={2} align="right">
                    {formatNumber(totalRuns)}
                  </TableSummary.Cell>
                  <TableSummary.Cell index={3} align="right">
                    {formatNumber(totalBreaks)}
                  </TableSummary.Cell>
                  <TableSummary.Cell index={4} align="right">
                    {formatNumber(total_running_time)}
                  </TableSummary.Cell>
                  <TableSummary.Cell index={5} align="right">
                    {formatNumber(total_idle_time)}
                  </TableSummary.Cell>
                  <TableSummary.Cell index={6} align="right">
                    {formatNumber(total_standby_time)}
                  </TableSummary.Cell>
                </TableSummary.Row>
              </TableSummary>
            );
          }}
        />
      </LockedFeature>
    </>
  );
};
